import styled from '@emotion/styled';
import { Divider, Slider } from '@mui/material';
import { Button, Label, Radio, ToggleSwitch } from 'flowbite-react';
import React, { useEffect, useMemo, useState } from 'react';
import { MultiValue } from 'react-select';
import Select, { SingleValue } from 'react-select';
import {
	Dropdown,
	MAX_GRAD_YEAR,
	MAX_LEVEL,
	MAX_PREDICTED_SALARY,
	MIN_GRAD_YEAR,
	MIN_LEVEL,
	MIN_PREDICTED_SALARY,
	isUserAccountManager,
	locationPreferenceOptions,
} from 'shared-utils';
import { useRecommendationsContext } from 'src/context/RecommendationsProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetPositionByIdQuery } from 'src/hooks/position.hooks';
import { useGetDropdownItemsByCategory, useGetLMSTechStacksQuery } from 'src/hooks/dropdownMenu.hooks';
import { ApplicationRoundType, FilterRecommendationsInput, UserPlacementTags } from 'types';

export const PrettoSlider = styled(Slider)({
	color: '#1C64F2',
	height: 8,
	'& .MuiSlider-rail': {
		background: '#E5E7EB',
		opacity: 1,
	},
	'& .MuiSlider-track': {
		border: 'none',
	},
	'& .MuiSlider-thumb': {
		height: 24,
		width: 24,
		backgroundColor: '#fff',
		border: '2px solid #E5E7EB',
		'&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
			boxShadow: 'inherit',
		},
		'&::before': {
			display: 'none',
		},
	},
	'& .MuiSlider-valueLabel': {
		lineHeight: '16px',
		fontSize: '12px',
		padding: 0,
		fontWeight: '500',
		fontFamily: 'Inter, sans-serif',
		width: 32,
		height: 32,
		top: '-35px',
		borderRadius: 0,
		color: '#28293D',
		backgroundColor: 'transparent',
		transformOrigin: 'bottom left',
		transform: 'none',
		'&::before': { display: 'none' },
		'&.MuiSlider-valueLabelOpen': {
			transform: 'none',
		},
	},
});

const RecruiterCandidatesFilters = ({
	positionId,
	currentTab,
	onClose,
	onApply,
	onClear,
	isApplying = false,
}: {
	positionId?: string;
	currentTab?: string;
	onClose?: () => void;
	onApply?: () => void;
	onClear?: () => void;
	isApplying?: boolean;
}) => {
	const { user } = useUserContext();
	const [filters, setFilters] = React.useState<FilterRecommendationsInput | null>();
	const [inputValue, setInputValue] = React.useState('');
	const [value, setValue] = React.useState<MultiValue<Dropdown>>([]);
	const [jobSeekerSubroles, setJobSeekerSubroles] = React.useState<MultiValue<Dropdown>>([]);
	const [placementStatuses, setPlacementStatuses] = React.useState<MultiValue<Dropdown>>([]);
	const [lmsTechStackFilters, setLmsTechStackFilters] = React.useState<MultiValue<Dropdown>>([]);
	const [currentRounds, setCurrentRounds] = React.useState<MultiValue<Dropdown>>([]);
	const [roundMovement, setRoundMovement] = React.useState<'NotStarted' | 'Ongoing' | 'Rejected' | 'ScreenRejected' | 'AutoRejected' | 'PlacedElsewhere' | 'Placed' | undefined>();
	const [recommendationTypeFilter, setRecommendationTypeFilter] = React.useState<
		'auto' | 'manual' | 'both' | 'you_might_be_interested' | undefined
	>();
	const [rejectedTypeFilter, setRejectedTypeFilter] = React.useState<'recommendations' | 'applicants' | 'both' | undefined>();
	const { setRecommendationsFilters, recommendationsFilters } = useRecommendationsContext();
	const { isLoading, data: positionData } = useGetPositionByIdQuery(positionId);
	// Fetch SubRole items from database (all active items, not filtered by showInMenu - for filtering)
	const { data: subRoleData } = useGetDropdownItemsByCategory('SUB_ROLE', undefined, true);
	const { data: lmsTechStacksData, isLoading: isLoadingLmsTechStacks } = useGetLMSTechStacksQuery(undefined, true);

	const [showInterestedCandidates, setShowInterestedCandidates] = useState(false);

	// Initialize filter state from existing filters and sync when filters change
	useEffect(() => {
		// Sync showInterestedCandidates
		setShowInterestedCandidates(recommendationsFilters?.showInterestedCandidates ?? false);

		// Sync recommendationTypeFilter
		// Treat "all" as no filter in UI state (same existing behavior).
		setRecommendationTypeFilter(
			recommendationsFilters?.recommendationTypeFilter === 'all'
				? undefined
				: recommendationsFilters?.recommendationTypeFilter ?? undefined,
		);

		// Sync rejectedTypeFilter
		setRejectedTypeFilter(recommendationsFilters?.rejectedTypeFilter ?? undefined);

		// Sync roundMovement - explicitly set to undefined if not present
		setRoundMovement(recommendationsFilters?.roundMovement ?? undefined);

		// Sync placementStatuses
		if (recommendationsFilters?.placementStatuses) {
			try {
				const statuses = JSON.parse(recommendationsFilters.placementStatuses);
				if (Array.isArray(statuses) && statuses.length > 0) {
					const statusOptions = statuses.map((status: string) => ({
						label: status.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').trim(),
						value: status,
					}));
					setPlacementStatuses(statusOptions);
				} else {
					setPlacementStatuses([]);
				}
			} catch (e) {
				setPlacementStatuses([]);
			}
		} else {
			setPlacementStatuses([]);
		}

		// Sync jobSeekerSubroles (Candidate Type)
		if (recommendationsFilters?.jobSeekerSubroles) {
			try {
				const subroles = JSON.parse(recommendationsFilters.jobSeekerSubroles);
				if (Array.isArray(subroles) && subroles.length > 0) {
					const subroleOptions = subroles.map((subrole: string) => ({
						label: subrole,
						value: subrole,
					}));
					setJobSeekerSubroles(subroleOptions);
				} else {
					setJobSeekerSubroles([]);
				}
			} catch (e) {
				setJobSeekerSubroles([]);
			}
		} else {
			setJobSeekerSubroles([]);
		}

		// Sync LMS Tech Stack filters
		if (recommendationsFilters?.lmsTechStackFilter) {
			try {
				const techStacks = JSON.parse(recommendationsFilters.lmsTechStackFilter);
				if (Array.isArray(techStacks) && techStacks.length > 0) {
					setLmsTechStackFilters(
						techStacks.map((techStack: string) => ({
							label: techStack,
							value: techStack,
						})),
					);
				} else {
					setLmsTechStackFilters([]);
				}
			} catch (e) {
				setLmsTechStackFilters([]);
			}
		} else {
			setLmsTechStackFilters([]);
		}

		// Sync locations
		if (recommendationsFilters?.locations) {
			try {
				const locations = JSON.parse(recommendationsFilters.locations);
				if (Array.isArray(locations) && locations.length > 0) {
					const locationOptions = locations.map((location: string) => ({
						label: location,
						value: location,
					}));
					setValue(locationOptions);
				} else {
					setValue([]);
				}
			} catch (e) {
				setValue([]);
			}
		} else {
			setValue([]);
		}

		// Sync currentRounds (Ongoing Rounds)
		if (recommendationsFilters?.currentRounds) {
			try {
				const rounds = JSON.parse(recommendationsFilters.currentRounds);
				if (Array.isArray(rounds) && rounds.length > 0) {
					const roundOptions = rounds.map((round: string) => ({
						label: round,
						value: round,
					}));
					setCurrentRounds(roundOptions);
				} else {
					setCurrentRounds([]);
				}
			} catch (e) {
				setCurrentRounds([]);
			}
		} else {
			setCurrentRounds([]);
		}

		// Sync roundType and expectedJoining
		setFilters(prev => {
			const updated = prev ? { ...prev } : {};
			if (recommendationsFilters?.roundType) {
				updated.roundType = recommendationsFilters.roundType;
			} else {
				delete updated.roundType;
			}
			if (recommendationsFilters?.expectedJoining) {
				updated.expectedJoining = recommendationsFilters.expectedJoining;
			} else {
				delete updated.expectedJoining;
			}
			return Object.keys(updated).length > 0 ? updated : null;
		});
	}, [recommendationsFilters]);

	const placementStatusOptions = useMemo(
		() =>
			Object.values(UserPlacementTags).map(status => ({
				label: status.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').trim(),
				value: status,
			})),
		[],
	);
	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				const filtersData = {
					...filters,
					showInterestedCandidates,
					recommendationTypeFilter: recommendationTypeFilter,
					rejectedTypeFilter: rejectedTypeFilter,
					positionId: positionId || '',
					locations: JSON.stringify(value.map(v => v.value)),
					jobSeekerSubroles: JSON.stringify(jobSeekerSubroles.map(v => v.value)),
					currentRounds: JSON.stringify(currentRounds.map(v => v.value)),
					placementStatuses: JSON.stringify(placementStatuses.map(v => v.value)),
					lmsTechStackFilter: JSON.stringify(lmsTechStackFilters.map(v => v.value)),
					roundMovement: roundMovement,
					graduationYearRange:
						filters?.graduationYearRange?.min === MIN_GRAD_YEAR && filters?.graduationYearRange?.max === MAX_GRAD_YEAR
							? undefined
							: filters?.graduationYearRange,
					predictedSalary:
						filters?.predictedSalary?.min === MIN_PREDICTED_SALARY &&
							filters?.predictedSalary?.max === MAX_PREDICTED_SALARY
							? undefined
							: filters?.predictedSalary,
					level: filters?.level?.min === MIN_LEVEL && filters?.level?.max === MAX_LEVEL ? undefined : filters?.level,
				};
				setRecommendationsFilters(filtersData);
				onApply && onApply();
				onClose && onClose();
			}}
		>
			{/* Filter for interested candidates */}
			<div className="min-h-[38px] flex items-center">
				<ToggleSwitch
					theme={{
						toggle: {
							checked: {
								color: {
									blue: 'bg-[#7777E7] border-[#7777E7]',
								},
							},
						},
					}}
					checked={showInterestedCandidates}
					label={'Interested Candidates Only'}
					onChange={() => {
						setShowInterestedCandidates(prev => !prev);
					}}
				/>
			</div>
			{/* Filter for recommendation type */}
			<div className="mt-[28px]" key={`recommendationType-${recommendationTypeFilter || 'none'}`}>
				<div className="mb-6 block">
					<Label htmlFor="recommendationType" className="font-[700] font-inter text-sm text-[#28293D]" value="Recommendation Type" />
				</div>
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-2">
						<Radio
							id="recommendationType-auto"
							name="recommendationType"
							value="auto"
							checked={recommendationTypeFilter === 'auto'}
							onChange={() => setRecommendationTypeFilter('auto')}
						/>
						<Label htmlFor="recommendationType-auto" className="text-sm font-[400] font-inter">
							Auto
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							id="recommendationType-manual"
							name="recommendationType"
							value="manual"
							checked={recommendationTypeFilter === 'manual'}
							onChange={() => setRecommendationTypeFilter('manual')}
						/>
						<Label htmlFor="recommendationType-manual" className="text-sm font-[400] font-inter">
							Manual
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							id="recommendationType-both"
							name="recommendationType"
							value="both"
							checked={recommendationTypeFilter === 'both'}
							onChange={() => setRecommendationTypeFilter('both')}
						/>
						<Label htmlFor="recommendationType-both" className="text-sm font-[400] font-inter">
							Both
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							id="recommendationType-interested-profile"
							name="recommendationType"
							value="you_might_be_interested"
							checked={recommendationTypeFilter === 'you_might_be_interested'}
							onChange={() => setRecommendationTypeFilter('you_might_be_interested')}
						/>
						<Label htmlFor="recommendationType-interested-profile" className="text-sm font-[400] font-inter">
							You might be interested
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							id="recommendationType-all"
							name="recommendationType"
							value="all"
							checked={!recommendationTypeFilter}
							onChange={() => setRecommendationTypeFilter(undefined)}
						/>
						<Label htmlFor="recommendationType-all" className="text-sm font-[400] font-inter">
							All
						</Label>
					</div>
				</div>
			</div>
			{/* Filter for rejected type (only shown when rejected tab is active) */}
			{currentTab === 'rejected' && (
				<div className="mt-[28px]" key={`rejectedType-${rejectedTypeFilter || 'none'}`}>
					<div className="mb-6 block">
						<Label htmlFor="rejectedType" className="font-[700] font-inter text-sm text-[#28293D]" value="Rejected Type" />
					</div>
					<div className="flex flex-col gap-2">
						<div className="flex items-center gap-2">
							<Radio
								id="rejectedType-recommendations"
								name="rejectedType"
								value="recommendations"
								checked={rejectedTypeFilter === 'recommendations'}
								onChange={() => setRejectedTypeFilter('recommendations')}
							/>
							<Label htmlFor="rejectedType-recommendations" className="text-sm font-[400] font-inter">
								Recommendations
							</Label>
						</div>
						<div className="flex items-center gap-2">
							<Radio
								id="rejectedType-applicants"
								name="rejectedType"
								value="applicants"
								checked={rejectedTypeFilter === 'applicants'}
								onChange={() => setRejectedTypeFilter('applicants')}
							/>
							<Label htmlFor="rejectedType-applicants" className="text-sm font-[400] font-inter">
								Applicants
							</Label>
						</div>
						<div className="flex items-center gap-2">
							<Radio
								id="rejectedType-both"
								name="rejectedType"
								value="both"
								checked={rejectedTypeFilter === 'both'}
								onChange={() => setRejectedTypeFilter('both')}
							/>
							<Label htmlFor="rejectedType-both" className="text-sm font-[400] font-inter">
								Both
							</Label>
						</div>
						<div className="flex items-center gap-2">
							<Radio
								id="rejectedType-all"
								name="rejectedType"
								value="all"
								checked={!rejectedTypeFilter}
								onChange={() => setRejectedTypeFilter(undefined)}
							/>
							<Label htmlFor="rejectedType-all" className="text-sm font-[400] font-inter">
								All
							</Label>
						</div>
					</div>
				</div>
			)}
			{/* Filter for Grad Year */}
			<div className="mt-[28px]">
				<div className="mb-6 block">
					<Label
						htmlFor="gradYear"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Candidate’s Graduation Year"
					/>
				</div>

				<PrettoSlider
					valueLabelDisplay="on"
					aria-label="Graduation Year"
					max={MAX_GRAD_YEAR}
					min={MIN_GRAD_YEAR}
					defaultValue={[MIN_GRAD_YEAR, MAX_GRAD_YEAR]}
					value={
						filters?.graduationYearRange?.min
							? [filters?.graduationYearRange?.min || MIN_GRAD_YEAR, filters?.graduationYearRange?.max || MAX_GRAD_YEAR]
							: [MIN_GRAD_YEAR, MAX_GRAD_YEAR]
					}
					onChange={(e: Event, newValue: number | number[]) => {
						if (!Array.isArray(newValue)) {
							return;
						}
						setFilters(prev => {
							return { ...prev, graduationYearRange: { min: newValue[0], max: newValue[1] } };
						});
					}}
				/>
			</div>

			<Divider />

			{/* Filter for Student Type */}
			{user?.role && isUserAccountManager(user?.role) && (
				<div className="mb-3 mt-[20px] whiteBgSelect">
					<div className="mb-4 block">
						<Label
							htmlFor="candidateType"
							className="font-[700] font-inter text-sm text-[#28293D]"
							value="Skill Filter"
						/>
					</div>

					<Select
						isMulti
						isClearable
						isLoading={isLoading}
						options={positionData?.position?.positionRequirement?.skillThresholds
							?.map(l => ({
								label: l?.displaySkill,
								value: l?.displaySkill,
							}))
							.concat(
								positionData?.position?.positionRequirement?.goodToHaveskills?.map(skill => ({
									label: skill.name,
									value: skill.name,
								})) || [],
							)}
						onChange={(vals: MultiValue<Dropdown>) => {
							setFilters(prev => {
								return {
									...prev,
									skillThreshold: {
										...prev?.skillThreshold,
										selectedSkills: vals?.map(el => el.value) || [''],
									} as FilterRecommendationsInput['skillThreshold'],
								};
							});
						}}
						menuPlacement="auto"
						className="[&>*]:!min-h-[46px]"
						classNamePrefix="my-select-plans"
						placeholder="eg. MasaiStudent"
						blurInputOnSelect={false}
						closeMenuOnSelect={false}
						value={
							filters?.skillThreshold?.selectedSkills?.length
								? filters?.skillThreshold?.selectedSkills?.map(el => ({ label: el, value: el }))
								: null
						}
					/>

					{filters?.skillThreshold?.selectedSkills?.length ? (
						<>
							<div className="mt-[28px]">
								<div className="mb-6 block">
									<Label
										htmlFor="level"
										className="font-[700] font-inter text-sm text-[#28293D]"
										value="Skill Threshold"
									/>
								</div>

								<PrettoSlider
									valueLabelDisplay="on"
									aria-label="skillThreshold"
									max={1000}
									min={1}
									defaultValue={[MIN_LEVEL, MAX_LEVEL]}
									value={[filters?.skillThreshold?.min || 1, filters?.skillThreshold?.max || 1000]}
									onChange={(e: Event, newValue: number | number[]) => {
										if (!Array.isArray(newValue)) {
											return;
										}
										setFilters(prev => {
											return {
												...prev,
												skillThreshold: {
													...prev?.skillThreshold,
													min: newValue[0],
													max: newValue[1],
												} as FilterRecommendationsInput['skillThreshold'],
											};
										});
									}}
								/>
							</div>
						</>
					) : null}
				</div>
			)}

			<Divider />

			{/* Filter for Student Type */}
			{user?.role && isUserAccountManager(user?.role) && (
				<div className="mb-3 mt-[20px] whiteBgSelect">
					<div className="mb-4 block">
						<Label
							htmlFor="candidateType"
							className="font-[700] font-inter text-sm text-[#28293D]"
							value="Candidate Type"
						/>
					</div>

					<Select
						isClearable
						isMulti
						options={subRoleData?.lmsTechStacks
							?.filter(item => ['MasaiStudent', 'MasaiAlum', 'CAP', 'IIT'].includes(item.name))
							.map(item => ({
								label: item.label || item.name, // Use label for display, fallback to name
								value: item.name, // Store name (string value) in database
							})) || []}
						onChange={(vals: MultiValue<Dropdown>) => {
							setJobSeekerSubroles(vals);
							setFilters(prev => {
								return { ...prev };
							});
						}}
						menuPlacement="auto"
						className="[&>*]:!min-h-[46px]"
						classNamePrefix="my-select-plans"
						placeholder="eg. MasaiStudent"
						blurInputOnSelect={false}
						closeMenuOnSelect={false}
						value={jobSeekerSubroles}
					/>
				</div>
			)}
			<div className="mb-3 mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label
						htmlFor="placementStatuses"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Placement Status"
					/>
				</div>

				<Select
					isClearable
					isMulti
					options={placementStatusOptions}
					onChange={(vals: MultiValue<Dropdown>) => {
						setPlacementStatuses(vals);
						setFilters(prev => {
							return { ...prev };
						});
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="Availability"
					blurInputOnSelect={false}
					closeMenuOnSelect={false}
					value={placementStatuses}
				/>
			</div>
			<div className="mb-3 mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label
						htmlFor="lmsTechStackFilter"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="LMS Tech stack"
					/>
				</div>

				<Select
					isClearable
					isMulti
					isLoading={isLoadingLmsTechStacks}
					options={
						lmsTechStacksData?.lmsTechStacks?.map(item => ({
							label: item.label || item.name,
							value: item.name,
						})) || []
					}
					onChange={(vals: MultiValue<Dropdown>) => {
						setLmsTechStackFilters(vals);
						setFilters(prev => {
							return { ...prev };
						});
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="eg. MERN, AI"
					blurInputOnSelect={false}
					closeMenuOnSelect={false}
					value={lmsTechStackFilters}
				/>
			</div>
			{/* Filter for Ongoing Rounds */}

			<div className="mb-3 mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label
						htmlFor="currentRounds"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Ongoing Rounds"
					/>
				</div>

				<Select
					isClearable
					isMulti
					options={
						[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(l => ({
							label: l.toString(),
							value: l.toString(),
						})) || []
					}
					onChange={(vals: MultiValue<Dropdown>) => {
						setCurrentRounds(vals);
						setFilters(prev => {
							return { ...prev };
						});
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="eg. 1"
					blurInputOnSelect={false}
					closeMenuOnSelect={false}
					value={currentRounds}
				/>
			</div>
			{/* Filter for Round Movement */}
			<div>
				<div className="mb-3 block">
					<Label htmlFor="roundMovement" className="font-[700] font-inter text-sm text-[#28293D]" value="Round Movement" />
				</div>

				<fieldset
					key={`roundMovement-${roundMovement || 'none'}`}
					className="flex flex-col max-w-md gap-4"
					onChange={e => {
						const target = e.target as HTMLInputElement;
						const value = target?.value as 'NotStarted' | 'Ongoing' | 'Rejected' | 'ScreenRejected' | 'AutoRejected' | 'PlacedElsewhere' | 'Placed' | undefined;
						setRoundMovement(value || undefined);
						setFilters(prev => {
							return { ...prev };
						});
					}}
				>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'NotStarted'}
							name="roundMovement"
							value={'NotStarted'}
							checked={roundMovement === 'NotStarted'}
						/>
						<Label htmlFor={'NotStarted'} className="text-sm font-[400] font-inter">
							Not Started
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'Ongoing'}
							name="roundMovement"
							value={'Ongoing'}
							checked={roundMovement === 'Ongoing'}
						/>
						<Label htmlFor={'Ongoing'} className="text-sm font-[400] font-inter">
							Ongoing Rounds
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'Rejected'}
							name="roundMovement"
							value={'Rejected'}
							checked={roundMovement === 'Rejected'}
						/>
						<Label htmlFor={'Rejected'} className="text-sm font-[400] font-inter">
							Rejected
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'ScreenRejected'}
							name="roundMovement"
							value={'ScreenRejected'}
							checked={roundMovement === 'ScreenRejected'}
						/>
						<Label htmlFor={'ScreenRejected'} className="text-sm font-[400] font-inter">
							Screen Rejected
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'AutoRejected'}
							name="roundMovement"
							value={'AutoRejected'}
							checked={roundMovement === 'AutoRejected'}
						/>
						<Label htmlFor={'AutoRejected'} className="text-sm font-[400] font-inter">
							Auto Rejected
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'PlacedElsewhere'}
							name="roundMovement"
							value={'PlacedElsewhere'}
							checked={roundMovement === 'PlacedElsewhere'}
						/>
						<Label htmlFor={'PlacedElsewhere'} className="text-sm font-[400] font-inter">
							Placed Elsewhere
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'Placed'}
							name="roundMovement"
							value={'Placed'}
							checked={roundMovement === 'Placed'}
						/>
						<Label htmlFor={'Placed'} className="text-sm font-[400] font-inter">
							Placed
						</Label>
					</div>
				</fieldset>
			</div>
			{/* Filter for Round Type */}
			<div>
				<div className="mt-6 block mb-2">
					<Label htmlFor="roundType" className="font-[700] font-inter text-sm text-[#28293D]" value="Round Type" />
				</div>

				<fieldset
					key={`roundType-${filters?.roundType || 'none'}`}
					className="flex flex-col max-w-md gap-4"
					onChange={e => {
						const target = e.target as HTMLInputElement;
						// ApplicationRoundType or Any
						const value = target?.value as ApplicationRoundType | 'Any';
						if (value) {
							setFilters({ ...filters, roundType: value });
						}
					}}
				>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'Assignment'}
							name="roundType"
							value={'Assignment'}
							checked={filters?.roundType === ApplicationRoundType.Assignment}
						/>
						<Label htmlFor={'Assignment'} className="text-sm font-[400] font-inter">
							Assigment
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'Interview'}
							name="roundType"
							value={'Interview'}
							checked={filters?.roundType === ApplicationRoundType.Interview}
						/>
						<Label htmlFor={'Interview'} className="text-sm font-[400] font-inter">
							Interview
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'OnlineTest'}
							name="roundType"
							value={'OnlineTest'}
							checked={filters?.roundType === ApplicationRoundType.OnlineTest}
						/>
						<Label htmlFor={'OnlineTest'} className="text-sm font-[400] font-inter">
							Online Test
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'CaseStudy'}
							name="roundType"
							value={'CaseStudy'}
							checked={filters?.roundType === ApplicationRoundType.CaseStudy}
						/>
						<Label htmlFor={'CaseStudy'} className="text-sm font-[400] font-inter">
							Case Study
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'Any'}
							name="roundType"
							value={'Any'}
							checked={filters?.roundType === 'Any'}
						/>
						<Label htmlFor={'Any'} className="text-sm font-[400] font-inter">
							Any
						</Label>
					</div>
				</fieldset>
			</div>
			{/* Available to join */}
			<div className="mt-6">
				<div className="mb-3 block">
					<Label
						htmlFor="expectedJoining"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Available to join"
					/>
				</div>

				<fieldset
					key={`expectedJoining-${filters?.expectedJoining || 'none'}`}
					className="flex flex-col max-w-md gap-4"
					onChange={e => {
						const target = e.target as HTMLInputElement;
						if (target?.value) {
							setFilters({ ...filters, expectedJoining: target?.value });
						}
					}}
				>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'Immediate'}
							name="expectedJoining"
							value={'Immediate'}
							checked={filters?.expectedJoining === 'Immediate'}
						/>
						<Label htmlFor={'Immediate'} className="text-sm font-[400] font-inter">
							Immediate
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'InOneMonth'}
							name="expectedJoining"
							value={'Within_1_month'}
							checked={filters?.expectedJoining === 'Within_1_month'}
						/>
						<Label htmlFor={'InOneMonth'} className="text-sm font-[400] font-inter">
							Within 1 month
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'InThreeMonth'}
							name="expectedJoining"
							value={'Within_3_months'}
							checked={filters?.expectedJoining === 'Within_3_months'}
						/>
						<Label htmlFor={'InThreeMonth'} className="text-sm font-[400] font-inter">
							Within 3 months
						</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'Any'}
							name="expectedJoining"
							value={'Any'}
							checked={filters?.expectedJoining === 'Any'}
						/>
						<Label htmlFor={'Any'} className="text-sm font-[400] font-inter">
							Any
						</Label>
					</div>
				</fieldset>
			</div>
			{/* <div className="mt-[28px]">
				<div className="mb-4 block">
					<Label
						htmlFor="expectedSalary"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Candidate’s expected salary"
					/>
				</div>

				<PrettoSlider
					valueLabelDisplay="on"
					aria-label="Expected Salary"
					max={80}
					min={1}
					// getAriaLabel={index => (index === 0 ? 'Minimum Expected Salary' : 'Maximum Expected Salary')}
					defaultValue={[12, 37]}
					onChange={(e: Event, newValue: number | number[]) => {
						if (!Array.isArray(newValue)) {
							return;
						}
						setFilters(prev => {
							return { ...prev, expectedSalary: { min: newValue[0], max: newValue[1] } };
						});
					}}
				/>

				<div className="flex justify-between text-sm text-[#555770] dark:text-inherit  mt-[-10px]">
					<p>1 LPA</p>
					<p>80 LPA</p>
				</div>
			</div> */}
			{/* <div className="mt-[28px]">
				<div className="mb-6 block">
					<Label
						htmlFor="predictedSalary"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Candidate’s predicted salary"
					/>
				</div>

				<PrettoSlider
					valueLabelDisplay="on"
					aria-label="Predicted Salary"
					max={80}
					min={1}
					// getAriaLabel={index => (index === 0 ? 'Minimum Predicted Salary' : 'Maximum Predicted Salary')}
					defaultValue={[MIN_PREDICTED_SALARY, MAX_PREDICTED_SALARY]}
					value={[
						filters?.predictedSalary?.min || MIN_PREDICTED_SALARY,
						filters?.predictedSalary?.max || MAX_PREDICTED_SALARY,
					]}
					onChange={(e: Event, newValue: number | number[]) => {
						if (!Array.isArray(newValue)) {
							return;
						}
						setFilters(prev => {
							return { ...prev, predictedSalary: { min: newValue[0], max: newValue[1] } };
						});
					}}
				/>

				<div className="flex justify-between text-sm text-[#555770] dark:text-inherit  mt-[-10px]">
					<p>1 LPA</p>
					<p>80 LPA</p>
				</div>
			</div> */}
			<div className="mt-[28px]">
				<div className="mb-6 block">
					<Label htmlFor="level" className="font-[700] font-inter text-sm text-[#28293D]" value="Candidate Level" />
				</div>

				<PrettoSlider
					valueLabelDisplay="on"
					aria-label="level"
					max={1000}
					min={1}
					// getAriaLabel={index => (index === 0 ? 'Minimum level' : 'Maximum level')}
					defaultValue={[MIN_LEVEL, MAX_LEVEL]}
					value={[filters?.level?.min || MIN_LEVEL, filters?.level?.max || MAX_LEVEL]}
					onChange={(e: Event, newValue: number | number[]) => {
						if (!Array.isArray(newValue)) {
							return;
						}
						setFilters(prev => {
							return { ...prev, level: { min: newValue[0], max: newValue[1] } };
						});
					}}
				/>
				{/* 
				<div className="flex justify-between text-sm text-[#555770] dark:text-inherit mt-[-10px]">
					<p>2</p>
					<p>1000</p>
				</div> */}
			</div>
			<div className="mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label
						htmlFor="location"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Preferred Job Location"
					/>
				</div>

				<Select
					isClearable
					isMulti
					options={locationPreferenceOptions.map(l => ({ label: l, value: l }))}
					onChange={(vals: MultiValue<Dropdown>) => {
						setValue(vals);
						setFilters(prev => {
							return { ...prev };
						});
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="Mumbai, Bangalore"
					blurInputOnSelect={false}
					closeMenuOnSelect={false}
					value={value}
				/>
			</div>
			<div className="flex flex-wrap gap-[20px] mt-[17px] sticky bottom-4">
				<Button
					type="reset"
					color="light"
					className="flex-1"
					onClick={() => {
						setValue([]);
						setInputValue('');
						setFilters(null);
						setPlacementStatuses([]);
						setLmsTechStackFilters([]);
						setRoundMovement(undefined);
						setRecommendationsFilters({} as FilterRecommendationsInput);
						onClear && onClear();
						onClose && onClose();
					}}
				>
					Clear
				</Button>
				<Button
					disabled={isApplying}
					isProcessing={isApplying}
					type="submit"
					color="purplePlaced"
					className="flex-1 bg-[#7777E7]"
				>
					Apply
				</Button>
			</div>
		</form>
	);
};

export default RecruiterCandidatesFilters;
